"use client";

import { Command } from "cmdk";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "../_docs/LangSwitch";
import { DOOR_LABEL, type Door } from "../_content/card";
import { Icon } from "../_family/Icon";
import { searchScore, searchValue, type SearchItem } from "../_lib/search-item";

const GROUPS: ReadonlyArray<{ kind: SearchItem["kind"]; heading: string }> = [
  { kind: "page", heading: "Pages" },
  { kind: "guide", heading: "Guides" },
  { kind: "line", heading: "Lines" },
  { kind: "route", heading: "Routes" },
  { kind: "event", heading: "Events" },
  { kind: "field", heading: "vestra.yaml" },
  { kind: "error", heading: "Errors" },
  { kind: "typescript", heading: "TypeScript" },
  { kind: "python", heading: "Python" },
  { kind: "cli", heading: "CLI" },
];

/** The groups that are one language's own (the CLI counts as one), and the language each is. */
const LANG_KIND: Partial<Record<SearchItem["kind"], Door>> = {
  typescript: "ts",
  python: "py",
  cli: "cli",
};
const OWN_LANGS: readonly Door[] = ["ts", "py", "cli"];

/** Asked for by a box on a page (the reference's hub): the search opens. */
const OPEN = "vestra:search";

/** The reference hub's box: it opens the search, and says what it finds. */
export function FindButton() {
  return (
    <button
      type="button"
      className="find"
      onClick={() => window.dispatchEvent(new Event(OPEN))}
    >
      <Icon name="search" />
      <span className="fl">Search routes, events, commands and guides</span>
      <kbd aria-hidden="true">⌘K</kbd>
    </button>
  );
}

/** A result's name: a route's method in its colour, then its path. */
function Title({ item }: { item: SearchItem }) {
  const m = item.kind === "route" ? /^([A-Z]+) (.+)$/.exec(item.title) : null;
  if (!m) return <span className="vsearch-t">{item.title}</span>;
  return (
    <span className="vsearch-t">
      <span className={`verb ${m[1]!.toLowerCase()}`}>{m[1]}</span> {m[2]}
    </span>
  );
}

/** The index, fetched once per page load and shared by every box (/developers/search.json, built with the site). */
let loading: Promise<readonly SearchItem[]> | null = null;
function loadIndex(): Promise<readonly SearchItem[]> {
  loading ??= fetch("/developers/search.json").then((r) => {
    if (!r.ok) throw new Error(`search index: HTTP ${r.status}`);
    return r.json() as Promise<SearchItem[]>;
  });
  // A failed load is tried again on the next open.
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

/**
 * The site's search (ruling K18): ⌘K, Ctrl K or / opens it, and it finds every page, line, route and event by
 * name, and every field, error, method and command of the reference (T13c). It wears the app's own dialog;
 * cmdk filters and moves through the results. Its index is fetched when the box is first wanted, not carried
 * by every page: `items` is for a box that already has them. The box opens outside the page's own tree, so
 * it takes the family's font classes with it (`fontClass`). It follows the reader's language (T13e-2b): the
 * entries of their SDK or CLI only (none for HTTP or MCP), until they ask for the rest.
 */
export function Search({
  items: given,
  fontClass = "",
}: {
  items?: readonly SearchItem[];
  fontClass?: string;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<readonly SearchItem[] | null>(
    given ?? null
  );
  const [failed, setFailed] = useState(false);
  const [everyLang, setEveryLang] = useState(false);
  const lang = useLang();
  const router = useRouter();
  // Opening the box fetches the index; hovering or focusing it starts the fetch early, so it is usually there.
  const want = useCallback(() => {
    if (given) return;
    setFailed(false);
    loadIndex().then(setItems, () => setFailed(true));
  }, [given]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target instanceof HTMLElement ? e.target : null;
      const typing =
        target !== null &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        want();
        setOpen((was) => !was);
      } else if (e.key === "/" && !typing) {
        e.preventDefault();
        want();
        setOpen(true);
      }
    };
    const onAsk = () => {
      want();
      setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN, onAsk);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN, onAsk);
    };
  }, [want]);

  const shown = (kind: SearchItem["kind"]): boolean => {
    const door = LANG_KIND[kind];
    return !door || everyLang || door === lang;
  };
  // The others, by name: "Python and CLI" for a TypeScript reader; all three for HTTP or MCP.
  const others = OWN_LANGS.filter((d) => d !== lang).map((d) => DOOR_LABEL[d]);
  const rest = `${others.slice(0, -1).join(", ")} and ${others.at(-1)}`;
  const toggle = OWN_LANGS.includes(lang)
    ? everyLang
      ? `Only ${DOOR_LABEL[lang]}`
      : `Show ${rest} too`
    : everyLang
      ? `Hide ${rest}`
      : `Show ${rest}`;

  const go = (href: string) => {
    setOpen(false);
    const [path, hash] = href.split("#");
    // The OpenAPI explorer is a static page behind a rewrite, and the console sits behind the edge's sign-in: load them whole.
    if (
      href.startsWith("/developers/reference/openapi") ||
      href.startsWith("/developers/console")
    )
      window.location.assign(href);
    // Somewhere on this page: the browser's own jump, so the page hears it and opens what it names. Named
    // again, the same place still opens.
    else if (hash && path === window.location.pathname) {
      if (window.location.hash === `#${hash}`)
        window.dispatchEvent(new HashChangeEvent("hashchange"));
      else window.location.hash = hash;
    } else router.push(href);
  };

  return (
    <>
      <button
        type="button"
        className="tb-search"
        aria-label="Search"
        aria-keyshortcuts="Meta+K Control+K /"
        onClick={() => {
          want();
          setOpen(true);
        }}
        onPointerEnter={want}
        onFocus={want}
      >
        <Icon name="search" />
        <span className="lbl" aria-hidden="true">
          Search
        </span>
        <kbd aria-hidden="true">⌘K</kbd>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          position="center-always"
          hideCloseButton
          className={`vsearch ${fontClass}`}
          overlayClassName="vsearch-veil"
          aria-describedby={undefined}
        >
          <DialogTitle className="vsearch-sr">
            Search the developer site
          </DialogTitle>
          <Command label="Search the developer site" filter={searchScore}>
            <Command.Input placeholder="Search lines, routes, fields, events, errors and methods" />
            <Command.List>
              {items ? (
                <Command.Empty>Nothing by that name.</Command.Empty>
              ) : (
                <Command.Loading>
                  {failed
                    ? "Search could not load. Close it and try again."
                    : "Opening the index..."}
                </Command.Loading>
              )}
              {GROUPS.filter((group) => shown(group.kind)).map((group) => (
                <Command.Group key={group.kind} heading={group.heading}>
                  {(items ?? [])
                    .filter((item) => item.kind === group.kind)
                    .map((item) => (
                      <Command.Item
                        key={item.href}
                        value={searchValue(item)}
                        keywords={[item.detail]}
                        onSelect={() => go(item.href)}
                      >
                        <Title item={item} />
                        <span className="vsearch-d">{item.detail}</span>
                      </Command.Item>
                    ))}
                </Command.Group>
              ))}
            </Command.List>
          </Command>
          <div className="vsearch-foot">
            <span>
              <kbd>↑</kbd>
              <kbd>↓</kbd> to move
            </span>
            <span>
              <kbd>↵</kbd> to open
            </span>
            <button type="button" onClick={() => setEveryLang((was) => !was)}>
              {toggle}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
